import type { JsonObject, TimestampMillis } from './common';

export const PROJECT_STATUSES = ['active', 'archived'] as const;

export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

export const SCRIPT_KINDS = ['main', 'module', 'library', 'config'] as const;

export type ScriptKind = (typeof SCRIPT_KINDS)[number];

export const VERSION_STATUSES = ['draft', 'published', 'yanked'] as const;

export type VersionStatus = (typeof VERSION_STATUSES)[number];

export const RELEASE_CHANNELS = ['stable', 'beta', 'nightly'] as const;

export type ReleaseChannel = (typeof RELEASE_CHANNELS)[number];

export const PROJECT_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,39}$/;

export const SEMVER_PATTERN = /^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/;

export const MAX_SCRIPT_SOURCE_BYTES = 2 * 1024 * 1024;

export interface Project {
  id: string;
  organization_id: string;
  slug: string;
  name: string;
  description: string;
  status: ProjectStatus;
  default_license_plan_id: string | null;
  checkpoint_flow_id: string | null;
  created_at: TimestampMillis;
  updated_at: TimestampMillis;
  archived_at: TimestampMillis | null;
  settings: JsonObject;
}

export interface Script {
  id: string;
  project_id: string;
  slug: string;
  name: string;
  kind: ScriptKind;
  entry_file: string;
  created_at: TimestampMillis;
  updated_at: TimestampMillis;
  latest_version_id: string | null;
}

export interface ScriptVersion {
  id: string;
  script_id: string;
  project_id: string;
  version: string;
  status: VersionStatus;
  min_game_version: string | null;
  checksum_sha256: string;
  size_bytes: number;
  changelog: string;
  created_at: TimestampMillis;
  published_at: TimestampMillis | null;
  yanked_at: TimestampMillis | null;
}

export interface Release {
  id: string;
  project_id: string;
  script_version_id: string;
  channel: ReleaseChannel;
  version: string;
  required_checkpoint_flow_id: string | null;
  requires_license: boolean;
  created_at: TimestampMillis;
  published_at: TimestampMillis | null;
  yanked_at: TimestampMillis | null;
  build_job_id: string | null;
  checksum_sha256: string;
}

export function isProjectStatus(value: string): value is ProjectStatus {
  return (PROJECT_STATUSES as readonly string[]).includes(value);
}

export function isVersionStatus(value: string): value is VersionStatus {
  return (VERSION_STATUSES as readonly string[]).includes(value);
}

export function isReleaseChannel(value: string): value is ReleaseChannel {
  return (RELEASE_CHANNELS as readonly string[]).includes(value);
}

export function isSemver(value: string): boolean {
  return SEMVER_PATTERN.test(value);
}

export function compareSemver(left: string, right: string): number {
  const parse = (value: string): { parts: number[]; pre: string | null } => {
    const [core, pre = null] = value.split('-');
    const parts = (core ?? '0.0.0').split('.').map(segment => Number.parseInt(segment, 10) || 0);
    return { parts, pre };
  };
  const a = parse(left);
  const b = parse(right);
  for (let index = 0; index < 3; index += 1) {
    const delta = (a.parts[index] ?? 0) - (b.parts[index] ?? 0);
    if (delta !== 0) {
      return delta < 0 ? -1 : 1;
    }
  }
  if (a.pre === b.pre) {
    return 0;
  }
  if (a.pre === null) {
    return 1;
  }
  if (b.pre === null) {
    return -1;
  }
  return a.pre < b.pre ? -1 : 1;
}
